<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Aplikasi', href: route('aplikasi.index') },
            { name: 'Edit Aplikasi' },
          ]"
        />

        <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
          <div
            class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
          >
            <h2
              class="flex items-center text-lg font-bold text-white sm:text-xl"
            >
              <i class="mr-2 fa-solid fa-edit sm:mr-3"></i>
              Edit Aplikasi
            </h2>
          </div>
          <div class="p-4 sm:p-6">
            <form class="space-y-6" @submit.prevent="submit">
              <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <!-- Left Column -->
                <div class="space-y-4">
                  <div>
                    <InputField
                      label="Nama Aplikasi"
                      icon="pen"
                      v-model="form.namaapps"
                      placeholder="Masukkan nama aplikasi..."
                      required
                    />
                    <InputError :message="form.errors.namaapps" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="URL Aplikasi"
                      icon="globe"
                      v-model="form.url"
                      placeholder="https://example.com"
                      required
                    />
                    <InputError :message="form.errors.url" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="Logo URL"
                      icon="image"
                      v-model="form.logo"
                      placeholder="URL logo aplikasi..."
                    />
                    <InputError :message="form.errors.logo" class="mt-2" />
                  </div>
                </div>

                <!-- Right Column -->
                <div class="space-y-4">
                  <div>
                    <InputLabel for="deskripsi" value="Deskripsi" />
                    <textarea
                      v-model="form.deskripsi"
                      id="deskripsi"
                      name="deskripsi"
                      rows="4"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Deskripsi aplikasi..."
                    ></textarea>
                    <InputError :message="form.errors.deskripsi" class="mt-2" />
                  </div>

                  <div>
                    <InputField
                      label="Nama Bisnis"
                      icon="building"
                      v-model="form.bussinesname"
                      placeholder="Nama bisnis/perusahaan..."
                    />
                    <InputError
                      :message="form.errors.bussinesname"
                      class="mt-2"
                    />
                  </div>

                  <div>
                    <InputField
                      label="Versi"
                      icon="tag"
                      v-model="form.version"
                      placeholder="1.0.0"
                    />
                    <InputError :message="form.errors.version" class="mt-2" />
                  </div>
                </div>
              </div>

              <!-- Theme Picker -->
              <div>
                <InputLabel value="Tema Tampilan (Theme)" />
                <p class="mt-1 mb-3 text-xs text-gray-500">
                  Pilih warna tema yang digunakan pada tampilan frontend
                  aplikasi.
                </p>
                <div class="flex flex-wrap gap-3">
                  <button
                    v-for="t in themes"
                    :key="t.value"
                    type="button"
                    @click="form.theme = t.value"
                    :class="[
                      'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors duration-200',
                      form.theme === t.value
                        ? 'border-gray-800 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-400',
                    ]"
                  >
                    <span
                      class="inline-block w-5 h-5 border border-white rounded-full shadow"
                      :style="{ backgroundColor: t.color }"
                    ></span>
                    {{ t.label }}
                    <i
                      v-if="form.theme === t.value"
                      class="text-gray-700 fa-solid fa-check"
                    ></i>
                  </button>
                </div>
                <InputError :message="form.errors.theme" class="mt-2" />
              </div>

              <!-- Submit Button -->
              <div class="flex justify-center pt-6">
                <PrimaryBtn
                  :disabled="form.processing"
                  class="px-8 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  <i class="mr-2 fa-solid fa-floppy-disk"></i>
                  {{ form.processing ? "Menyimpan..." : "Update Aplikasi" }}
                </PrimaryBtn>
              </div>
            </form>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { useForm, usePage } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputField from "@/Components/InputField.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import Swal from "sweetalert2";

defineProps({
  aplikasi: {
    type: Object,
    required: true,
  },
});

const { aplikasi } = usePage().props;

const form = useForm({
  namaapps: aplikasi.namaapps || "",
  url: aplikasi.url || "",
  logo: aplikasi.logo || "",
  deskripsi: aplikasi.deskripsi || "",
  bussinesname: aplikasi.bussinesname || "",
  version: aplikasi.version || "",
  theme: aplikasi.theme || "maroon",
});

const themes = [
  { value: "maroon", label: "Maroon", color: "#cc4444" },
  { value: "blue", label: "Blue", color: "#2563eb" },
  { value: "emerald", label: "Emerald", color: "#059669" },
  { value: "violet", label: "Violet", color: "#7c3aed" },
  { value: "teal", label: "Teal", color: "#0d9488" },
];

const submit = () => {
  // Show loading
  Swal.fire({
    title: "Menyimpan...",
    text: "Sedang memperbarui aplikasi",
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });

  form.put(route("aplikasi.update", aplikasi.id), {
    onSuccess: () => {
      Swal.fire({
        title: "Berhasil!",
        text: "Aplikasi berhasil diperbarui",
        icon: "success",
        confirmButtonText: "OK",
      });
    },
    onError: () => {
      Swal.fire({
        title: "Error!",
        text: "Terjadi kesalahan saat memperbarui aplikasi",
        icon: "error",
        confirmButtonText: "OK",
      });
    },
  });
};
</script>
